import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { requestDataDeletion } from '../api/api';
import './LegalPages.css';

function DataDeletion() {
  const [formData, setFormData] = useState({
    email: '',
    accountType: '',
    reason: '',
    confirmDelete: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await requestDataDeletion(formData);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="legal-page">
      <Navbar />
      
      <div className="legal-container">
        <div className="legal-header">
          <h1>Data Deletion Request</h1>
          <p className="last-updated">Your Data, Your Control</p>
        </div>

        <div className="legal-content">
          <section className="legal-section">
            <h2>Delete Your Data</h2>
            <p>
              At Milan AI, we respect your right to control your personal data. This page allows 
              you to request deletion of your account and associated data from our platform.
            </p>
          </section>

          <section className="legal-section">
            <h2>What Gets Deleted</h2>
            <p>When you request data deletion, the following will be permanently removed:</p>
            
            <div className="deletion-info-grid">
              <div className="deletion-card">
                <div className="deletion-card-header">
                  <span className="deletion-icon">👤</span>
                  <h3>Account Information</h3>
                </div>
                <ul>
                  <li>Your profile information (name, email, bio)</li>
                  <li>Login credentials and authentication tokens</li>
                  <li>Account settings and preferences</li>
                </ul>
              </div>

              <div className="deletion-card">
                <div className="deletion-card-header">
                  <span className="deletion-icon">📸</span>
                  <h3>Instagram Data</h3>
                </div>
                <ul>
                  <li>Connected Instagram profile information</li>
                  <li>Engagement metrics and analytics</li>
                  <li>Audience demographic data</li>
                  <li>Instagram access tokens</li>
                </ul>
              </div>

              <div className="deletion-card">
                <div className="deletion-card-header">
                  <span className="deletion-icon">📊</span>
                  <h3>Campaign History</h3>
                </div>
                <ul>
                  <li>Campaign participation records</li>
                  <li>Content submissions</li>
                  <li>Communication history</li>
                  <li>Performance data</li>
                </ul>
              </div>

              <div className="deletion-card">
                <div className="deletion-card-header">
                  <span className="deletion-icon">🎯</span>
                  <h3>Performance Hub Data</h3>
                </div>
                <ul>
                  <li>Uploaded creator lists (handles, emails, post URLs)</li>
                  <li>Performance Hub campaigns and creator rows</li>
                  <li>Metric snapshots and CRM shortlist entries</li>
                  <li>Plan entitlements and billing-event history</li>
                  <li>Performance Hub Instagram access tokens</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="legal-section">
            <h2>What We May Retain</h2>
            <p className="highlight-box">
              <strong>Legal Requirements:</strong> Some data may be retained for legal, tax, or 
              regulatory compliance purposes. This includes:
            </p>
            <ul>
              <li>Financial transaction records (retained for 7 years per tax regulations)</li>
              <li>Data required for legal claims or disputes</li>
              <li>Anonymized, aggregated data that cannot identify you</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>Pending Payments Warning</h2>
            <div className="warning-box" style={{
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderLeft: '4px solid #f59e0b',
              borderRadius: '0 8px 8px 0',
              padding: '16px 20px',
              marginBottom: '20px'
            }}>
              <p style={{ margin: 0, marginBottom: '12px' }}>
                <strong style={{ color: '#f59e0b' }}>Important for Creators:</strong>
              </p>
              <p style={{ margin: 0 }}>
                If you have pending payments that have not yet been processed, these payments 
                will be <strong>forfeited</strong> upon account deletion. We strongly recommend 
                waiting until all your payments are fully processed before requesting account 
                deletion. Once your account is deleted, we cannot process any outstanding payments.
              </p>
            </div>
            <p>
              Please check your dashboard to ensure you have no pending campaigns or payments 
              before proceeding with deletion.
            </p>
          </section>

          <section className="legal-section">
            <h2>Deletion Timeline</h2>
            <div className="timeline">
              <div className="timeline-item">
                <span className="timeline-badge">1</span>
                <div className="timeline-content">
                  <h4>Request Received</h4>
                  <p>We acknowledge your request within 24 hours</p>
                </div>
              </div>
              <div className="timeline-item">
                <span className="timeline-badge">2</span>
                <div className="timeline-content">
                  <h4>Verification</h4>
                  <p>We verify your identity (1-3 business days)</p>
                </div>
              </div>
              <div className="timeline-item">
                <span className="timeline-badge">3</span>
                <div className="timeline-content">
                  <h4>Processing</h4>
                  <p>Data deletion is processed (up to 30 days)</p>
                </div>
              </div>
              <div className="timeline-item">
                <span className="timeline-badge">4</span>
                <div className="timeline-content">
                  <h4>Confirmation</h4>
                  <p>You receive confirmation of deletion</p>
                </div>
              </div>
            </div>
          </section>

          <section className="legal-section">
            <h2>Alternative Options</h2>
            <p>Before deleting your account, consider these alternatives:</p>
            <ul>
              <li><strong>Disconnect Instagram:</strong> Remove Instagram connection while keeping your account</li>
              <li><strong>Deactivate Account:</strong> Temporarily disable your account (data preserved)</li>
              <li><strong>Export Data:</strong> Download a copy of your data before deletion</li>
            </ul>
          </section>

          {!submitted ? (
            <section className="legal-section deletion-form-section">
              <h2>Request Data Deletion</h2>
              
              <form onSubmit={handleSubmit} className="deletion-form">
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter the email associated with your account"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="accountType">Account Type *</label>
                  <select
                    id="accountType"
                    name="accountType"
                    value={formData.accountType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select account type</option>
                    <option value="creator">Creator</option>
                    <option value="brand">Brand</option>
                    <option value="both">Both (Creator & Brand)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="reason">Reason for Deletion (Optional)</label>
                  <textarea
                    id="reason"
                    name="reason"
                    value={formData.reason}
                    onChange={handleInputChange}
                    placeholder="Help us improve by sharing why you're leaving..."
                    rows="3"
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="confirmDelete"
                      checked={formData.confirmDelete}
                      onChange={handleInputChange}
                      required
                    />
                    <span className="checkmark"></span>
                    I understand that this action is irreversible and all my data will be permanently deleted.
                  </label>
                </div>

                {error && (
                  <div className="error-message" style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '16px',
                    color: '#ef4444'
                  }}>
                    {error}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="delete-btn"
                  disabled={!formData.confirmDelete || loading}
                >
                  {loading ? 'Processing...' : 'Submit Deletion Request'}
                </button>
              </form>
            </section>
          ) : (
            <section className="legal-section success-section">
              <div className="success-message">
                <span className="success-icon">✓</span>
                <h2>Request Submitted</h2>
                <p>
                  Your data deletion request has been received. We will verify your identity 
                  and process your request within 30 days.
                </p>
                <p>
                  You will receive a confirmation email at <strong>{formData.email}</strong> 
                  once the deletion is complete.
                </p>
              </div>
            </section>
          )}

          <section className="legal-section">
            <h2>Instagram App Removal</h2>
            <p>To fully disconnect Milan AI from your Instagram account:</p>
            <ol>
              <li>Open Instagram and go to your profile</li>
              <li>Tap Settings → Apps and Websites</li>
              <li>Find "Milan AI" in the list of active apps</li>
              <li>Tap "Remove" to revoke access</li>
            </ol>
            <p className="highlight-box">
              <strong>Note:</strong> Removing Milan AI from Instagram will immediately revoke our 
              access to your Instagram data, even if your account deletion request is still processing.
            </p>
          </section>

          <section className="legal-section">
            <h2>Contact Us</h2>
            <p>
              If you have questions about data deletion or need assistance, please reach out 
              to us through our <a href="/contact">Contact Page</a>.
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default DataDeletion;
